import { useState } from "react"

/**
 * Inline name + email capture form. Rendered as Pearl's first attachment on
 * a fresh conversation when we don't already have an identity for the
 * customer. Blocks the conversation (no chips, disabled composer) until
 * submitted — see FinChatWidget / ChatWidget gating logic. Replaces the
 * previous prompt-only "Pearl asks for name first" flow, which had variance.
 *
 * Why a single combined form (not two sequential asks): every extra round
 * trip is a bail opportunity. One form + one Submit captures both fields
 * in the same moment the customer commits.
 */
interface Props {
  onSubmit: (name: string, email: string, phone: string) => Promise<void> | void
  pending?: boolean
  /** Optional one-line intro shown above the fields (e.g. product-aware). */
  intro?: string
  /** Server-side rejection message (bad format / disposable / junk email).
   *  The SERVER is the source of truth — disposable/junk addresses pass the
   *  client format check but fail server validation; this surfaces that
   *  verdict inline under the email field. */
  serverError?: string | null
}

// Client-side format mirror of the server's isValidCustomerEmail format gate.
// Stricter than \S+@\S+\.\S+ so the instant feedback matches the server: a
// dotted domain whose TLD is >=2 alpha chars (rejects "a@a", "foo@bar"). The
// disposable/junk checks stay server-only.
const EMAIL_REGEX =
  /^[^\s@]+@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/i

// Client mirror of isValidCustomerPhone's format gate: 10–15 digits, not a
// single repeated digit. The server re-validates + normalizes to E.164.
function phoneLooksValid(raw: string): boolean {
  const d = raw.replace(/\D/g, "")
  if (d.length < 10 || d.length > 15) return false
  const core = d.length === 11 && d.startsWith("1") ? d.slice(1) : d
  return !/^(\d)\1+$/.test(core)
}

export function IdentityCaptureForm({
  onSubmit,
  pending = false,
  intro,
  serverError,
}: Props) {
  const [name, setName] = useState("")
  const [contact, setContact] = useState("")
  const [touched, setTouched] = useState(false)

  const trimmedName = name.trim()
  const trimmedContact = contact.trim()
  const nameValid = trimmedName.length > 0
  // ONE box for both: an "@" means treat it as an email, otherwise as a mobile
  // number. We route the value to the right field on submit.
  const looksEmail = trimmedContact.includes("@")
  const asEmail = trimmedContact.toLowerCase()
  const emailValid = looksEmail && EMAIL_REGEX.test(asEmail)
  const phoneValid = !looksEmail && phoneLooksValid(trimmedContact)
  const contactEntered = trimmedContact.length > 0
  const contactValid = emailValid || phoneValid
  const canSubmit = nameValid && contactValid && !pending

  // Styling notes:
  //  - All form text forced WHITE (color: #fff) — the widget can render on
  //    light or dark themes and the form sits on the dark panel; without an
  //    explicit color, browser defaults inherited grey from somewhere up the
  //    DOM and made fields hard to read.
  //  - Field borders bumped from 0.12 alpha to 0.28 alpha + 1.5px width so
  //    they read as real inputs, not glass dividers.
  //  - Inline styles instead of CSS module because the widget bundle ships
  //    these styles isolated; CSS module class wins over storefront CSS but
  //    inline still wins over both. Defensive.
  const fieldStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    // 16px MANDATORY: this is the gate every anonymous visitor clears before
    // chatting; iOS Safari auto-zooms the page on focus of any <16px input,
    // jolting the first interaction in the funnel.
    fontSize: 16,
    fontFamily: "inherit",
    // SUMMER 2026 light theme (2026-07-27): navy ink on white fields.
    color: "#06243a",
    background: "#ffffff",
    border: "1.5px solid rgba(6,36,58,0.22)",
    outline: "none",
    boxSizing: "border-box",
  }
  return (
    <form
      data-pearl-identity-form
      onSubmit={(e) => {
        e.preventDefault()
        setTouched(true)
        if (!canSubmit) return
        onSubmit(
          trimmedName,
          emailValid ? asEmail : "",
          phoneValid ? trimmedContact : "",
        )
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
        padding: 14,
        marginTop: 8,
        borderRadius: 12,
        background: "rgba(109,195,247,0.10)",
        border: "1px solid rgba(6,36,58,0.10)",
        backdropFilter: "blur(12px)",
        color: "#06243a",
        boxShadow: "0 1px 2px rgba(6,36,58,0.06), 0 8px 24px -12px rgba(6,36,58,0.12)",
      }}
    >
      {/*
        Scoped <style> block for the `::placeholder` pseudo — inline styles
        can't target pseudo-elements, and the default browser placeholder
        color (grey) is unreadable against the dark glass form background.
        Scoped via the data attribute so the rule can't leak to other
        inputs in the widget.
      */}
      <style>{`
        form[data-pearl-identity-form] input::placeholder {
          color: rgba(6,36,58,0.48);
          opacity: 1;
        }
        form[data-pearl-identity-form] input:focus {
          border-color: #6dc3f7;
        }
      `}</style>
      {intro && (
        <div style={{ fontSize: 13, opacity: 0.9, marginBottom: 4, color: "#06243a" }}>
          {intro}
        </div>
      )}
      <input
        type="text"
        placeholder="Your name"
        aria-label="Your name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        autoComplete="given-name"
        disabled={pending}
        style={fieldStyle}
      />
      {/* ONE box for EMAIL or MOBILE — the customer types whichever's easiest
          and we route it to the right field on submit (#11). Keeps the gate to
          two fields total. */}
      <input
        type="text"
        placeholder="Email or mobile number"
        aria-label="Email or mobile number"
        value={contact}
        onChange={(e) => setContact(e.target.value)}
        autoComplete="email"
        disabled={pending}
        style={fieldStyle}
      />
      {touched && !nameValid && (
        <div style={{ fontSize: 12, color: "#c0392b" }}>Please enter your name.</div>
      )}
      {touched && nameValid && contactEntered && !contactValid && (
        <div style={{ fontSize: 12, color: "#c0392b" }}>
          Enter a valid email or mobile number.
        </div>
      )}
      {touched && nameValid && !contactEntered && (
        <div style={{ fontSize: 12, color: "#c0392b" }}>
          Add an email or mobile so we can follow up.
        </div>
      )}
      {/* Server-side rejection (disposable / junk email) — shown when the
          client check passed but the server still said no. */}
      {serverError && contactValid && (
        <div style={{ fontSize: 12, color: "#c0392b" }}>{serverError}</div>
      )}
      <button
        type="submit"
        disabled={!canSubmit}
        style={{
          padding: "10px 14px",
          borderRadius: 8,
          fontSize: 14,
          fontFamily: "inherit",
          fontWeight: 600,
          color: canSubmit ? "#06243a" : "rgba(6,36,58,0.45)",
          background: canSubmit
            ? "linear-gradient(135deg,#6dc3f7,#8fd2f9)"
            : "rgba(6,36,58,0.08)",
          border: "none",
          cursor: canSubmit ? "pointer" : "not-allowed",
          opacity: canSubmit ? 1 : 0.6,
          marginTop: 2,
          transition: "opacity 0.15s",
        }}
      >
        {pending ? "Saving…" : "Continue"}
      </button>
    </form>
  )
}
